/**
 * Planner geometry: pure helpers the stop list, the header and the tests use.
 *
 *  • formatKm / formatDrive  — one number format everywhere
 *  • findBacktrack           — a leg that heads well away from where the trip ends
 *  • optimiseStopOrder       — shortest order with the start and the end kept
 *  • moveStopTo              — drag-and-drop reorder by index
 *  • routeThumbnail          — a tiny SVG path of the trip, for the header
 */
import { haversineDistanceKm } from '../../utils/tripPlanner';

type Pt = { lat?: number; lng?: number };
type Located = { lat: number; lng: number };

export const hasCoords = <T extends Pt>(s: T): s is T & Located =>
  typeof s.lat === 'number' && typeof s.lng === 'number' && Number.isFinite(s.lat) && Number.isFinite(s.lng);

const km = (a: Located, b: Located) => haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);

export function formatKm(value?: number): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '';
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString('en-AU')} km`;
}

export function formatDrive(minutes?: number): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return '';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

/** Straight-line length of the trip in this order (km). Stops without coords are skipped. */
export function pathKm(stops: Pt[]): number {
  const pts = stops.filter(hasCoords);
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) total += km(pts[i - 1], pts[i]);
  return total;
}

export interface Backtrack {
  /** Index of the stop the bad leg starts from. */
  fromIndex: number;
  toIndex: number;
  /** How much further from the destination the leg leaves you (km). */
  awayKm: number;
}

/**
 * The worst leg that drives well AWAY from the trip's destination: after it
 * you are `awayKm` further from the end than before it. One-way trips only —
 * a loop (start ≈ end) is meant to head away first.
 */
export function findBacktrack(stops: Pt[], thresholdKm = 150): Backtrack | null {
  if (stops.length < 3) return null;
  const first = stops[0];
  const end = stops[stops.length - 1];
  if (!hasCoords(end) || !hasCoords(first)) return null;
  if (km(first, end) < 50) return null; // a loop
  let worst: Backtrack | null = null;
  for (let i = 0; i < stops.length - 2; i += 1) {
    const a = stops[i];
    const b = stops[i + 1];
    if (!hasCoords(a) || !hasCoords(b)) continue;
    const away = km(b, end) - km(a, end);
    if (away > thresholdKm && (!worst || away > worst.awayKm)) {
      worst = { fromIndex: i, toIndex: i + 1, awayKm: Math.round(away) };
    }
  }
  return worst;
}

/**
 * Shortest straight-line order that keeps the first and last stop where they
 * are. Nearest-neighbour seed, then 2-opt over the middle. Stops without
 * coordinates keep their relative order at the end of the middle section.
 */
export function optimiseStopOrder<T extends Pt>(stops: T[]): T[] {
  if (stops.length < 4) return stops;
  const start = stops[0];
  const end = stops[stops.length - 1];
  if (!hasCoords(start) || !hasCoords(end)) return stops;
  const middle = stops.slice(1, -1);
  const located = middle.filter(hasCoords) as (T & Located)[];
  const unlocated = middle.filter((s) => !hasCoords(s));
  if (located.length < 2) return stops;

  // Nearest neighbour from the start.
  const remaining = [...located];
  const order: (T & Located)[] = [];
  let cur: Located = start;
  while (remaining.length) {
    let bi = 0;
    let bd = Infinity;
    remaining.forEach((s, i) => {
      const d = km(cur, s);
      if (d < bd) {
        bd = d;
        bi = i;
      }
    });
    cur = remaining[bi];
    order.push(remaining.splice(bi, 1)[0]);
  }

  // 2-opt with fixed endpoints.
  const route: Located[] = [start, ...order, end];
  let improved = true;
  let guard = 0;
  while (improved && guard < 50) {
    improved = false;
    guard += 1;
    for (let i = 1; i < route.length - 2; i += 1) {
      for (let k = i + 1; k < route.length - 1; k += 1) {
        const before = km(route[i - 1], route[i]) + km(route[k], route[k + 1]);
        const after = km(route[i - 1], route[k]) + km(route[i], route[k + 1]);
        if (after + 1e-6 < before) {
          const seg = route.slice(i, k + 1).reverse();
          route.splice(i, seg.length, ...seg);
          improved = true;
        }
      }
    }
  }
  const optimisedMiddle = route.slice(1, -1) as (T & Located)[];
  return [start, ...optimisedMiddle, ...unlocated, end];
}

/**
 * Shortest order that keeps only the END fixed: tries each located stop as the
 * start. For a trip whose first leg drives away from where it ends, this is
 * the order that begins at the far end and drives through.
 */
export function optimiseWithFreeStart<T extends Pt>(stops: T[]): T[] {
  if (stops.length < 3) return stops;
  const end = stops[stops.length - 1];
  const candidates = stops.slice(0, -1).filter(hasCoords);
  if (!hasCoords(end) || candidates.length < 2) return stops;
  let best = stops;
  let bestKm = pathKm(stops);
  for (const start of candidates) {
    const rest = stops.slice(0, -1).filter((s) => s !== start);
    const trial = rest.length >= 2 ? optimiseStopOrder([start, ...rest, end]) : [start, ...rest, end];
    const k = pathKm(trial);
    if (k + 1e-6 < bestKm) {
      best = trial;
      bestKm = k;
    }
  }
  return best;
}

/**
 * A better order, if there is one worth offering.
 *  • keepStart (default): same start and end, middle reordered; worth it from `minSavingKm`.
 *  • keepStart false: only the end fixed; worth it from 15% of the trip or 50 km.
 */
export function optimisationSaving<T extends Pt & { id?: string }>(
  stops: T[],
  minSavingKm = 10,
  opts: { keepStart?: boolean } = {}
): { order: T[]; savedKm: number } | null {
  const keepStart = opts.keepStart !== false;
  const order = keepStart ? optimiseStopOrder(stops) : optimiseWithFreeStart(stops);
  if (!keepStart) minSavingKm = Math.max(minSavingKm, 50, pathKm(stops) * 0.15);
  if (order === stops) return null;
  const same = order.every((s, i) => s === stops[i]);
  if (same) return null;
  const savedKm = pathKm(stops) - pathKm(order);
  if (savedKm < minSavingKm) return null;
  return { order, savedKm: Math.round(savedKm) };
}

/** Move the stop at `from` so it lands at `to` (indexes in the original list). */
export function moveStopTo<T extends { locked?: boolean }>(stops: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= stops.length || to >= stops.length) return stops;
  if (stops[from]?.locked) return stops;
  const next = [...stops];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * A small SVG polyline of the trip (equirectangular, fitted to a w×h box with
 * padding). Returns the points for the line and each stop. Null when fewer
 * than two stops have coordinates.
 */
export function routeThumbnail(
  stops: Pt[],
  w = 48,
  h = 48,
  pad = 7
): { points: { x: number; y: number }[]; path: string } | null {
  const pts = stops.filter(hasCoords);
  if (pts.length < 2) return null;
  const midLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
  const kx = Math.cos((midLat * Math.PI) / 180);
  const xs = pts.map((p) => p.lng * kx);
  const ys = pts.map((p) => -p.lat);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = Math.max(maxX - minX, 1e-6);
  const spanY = Math.max(maxY - minY, 1e-6);
  const scale = Math.min((w - pad * 2) / spanX, (h - pad * 2) / spanY);
  const offX = (w - spanX * scale) / 2;
  const offY = (h - spanY * scale) / 2;
  const points = pts.map((_, i) => ({
    x: Math.round((offX + (xs[i] - minX) * scale) * 10) / 10,
    y: Math.round((offY + (ys[i] - minY) * scale) * 10) / 10,
  }));
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ');
  return { points, path };
}

/** "Thu 12 Nov" for a YYYY-MM-DD date. */
export function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Identity of a trip's stops for "is this still what was saved?": names,
 * rounded coordinates and nights, in order. Ids are left out on purpose —
 * the server re-mints them on save.
 */
export function stopsSignature(stops: { name?: string; lat?: number; lng?: number; nights?: number }[]): string {
  return stops
    .map((s) => {
      const where = hasCoords(s) ? `${s.lat.toFixed(3)},${s.lng.toFixed(3)}` : '';
      return `${(s.name || '').trim().toLowerCase()}@${where}#${s.nights ?? 0}`;
    })
    .join('|');
}
